import type { AuthSession, Me, Membership } from '@walkkit/shared';

export type StoredSession = {
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: string;
  user: Me;
  /** The business whose data the dashboard is showing (sent as X-Business-Id). */
  activeBusinessId: string | null;
};

export type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export type SessionStore = {
  get(): StoredSession | null;
  set(next: StoredSession | null): void;
  subscribe(listener: () => void): () => void;
};

const ROLE_RANK: Record<Membership['role'], number> = { owner_admin: 0, staff: 1, client: 2 };

/** Owner/staff memberships first; clients last. */
export function pickDefaultBusiness(me: Me, preferred?: string | null): string | null {
  if (preferred && me.memberships.some((m) => m.businessId === preferred)) return preferred;
  const sorted = [...me.memberships].sort((a, b) => ROLE_RANK[a.role] - ROLE_RANK[b.role]);
  return sorted[0]?.businessId ?? null;
}

export function toStoredSession(auth: AuthSession, preferredBusinessId?: string | null): StoredSession {
  return {
    accessToken: auth.accessToken,
    refreshToken: auth.refreshToken,
    accessExpiresAt: auth.accessExpiresAt,
    user: auth.user,
    activeBusinessId: pickDefaultBusiness(auth.user, preferredBusinessId),
  };
}

export function activeMembership(s: StoredSession | null): Membership | null {
  if (!s) return null;
  return s.user.memberships.find((m) => m.businessId === s.activeBusinessId) ?? null;
}

function isStoredSession(v: unknown): v is StoredSession {
  if (!v || typeof v !== 'object') return false;
  const s = v as Partial<StoredSession>;
  return typeof s.accessToken === 'string' && typeof s.refreshToken === 'string' && !!s.user && Array.isArray(s.user.memberships);
}

/** Session persisted in (local)Storage and shared across tabs via the storage event. */
export function createSessionStore(storage: KeyValueStorage | null, key = 'walkkit.session'): SessionStore {
  const listeners = new Set<() => void>();
  let cache: StoredSession | null | undefined;

  const read = (): StoredSession | null => {
    if (cache !== undefined) return cache;
    try {
      const raw = storage?.getItem(key);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      cache = isStoredSession(parsed) ? parsed : null;
    } catch {
      cache = null;
    }
    return cache;
  };

  const emit = () => listeners.forEach((l) => l());

  if (typeof window !== 'undefined' && storage === window.localStorage) {
    window.addEventListener('storage', (e) => {
      if (e.key !== key) return;
      cache = undefined;
      emit();
    });
  }

  return {
    get: read,
    set(next) {
      cache = next;
      try {
        if (next) storage?.setItem(key, JSON.stringify(next));
        else storage?.removeItem(key);
      } catch {
        // Private mode / quota: keep the in-memory copy.
      }
      emit();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
